/**
 * StepIndicator — primitive générique de progression d'un wizard.
 *
 * Volontairement non-spécifique au wizard projet : prend `steps: string[]` en
 * prop. Réutilisable par le futur intake dynamique (qui peut avoir 1, 5 ou 12
 * étapes selon le formulaire tenant-specific).
 *
 * A11y :
 *  - <ol> sémantique pour la liste ordonnée
 *  - aria-current="step" sur l'étape active
 *  - aria-label sur la nav (i18n)
 *
 * 9.T.5.v3.6 QA D — navigation cliquable optionnelle backward-compat. Si le
 * consumer passe `onStepClick` + `canGoToStep`, chaque step accessible devient
 * un `<button>` clavier-accessible avec focus-visible ring. Sinon comportement
 * legacy `<span>` inchangé (parity `_intake_mvp`).
 */

import { useTranslation } from "react-i18next";

import { cn } from "../lib/cn";

type Props = {
  /** Labels des étapes, dans l'ordre. */
  steps: string[];
  /** Index 0-based de l'étape active. */
  currentIndex: number;
  /** ARIA label de la nav (ex. t('projects.intake.progress')). Optionnel. */
  ariaLabel?: string;
  /**
   * Handler optionnel pour saut de step (9.T.5.v3.6). Si présent, les steps
   * pour lesquels `canGoToStep(idx)` retourne true deviennent cliquables.
   * Sinon, `<span>` legacy inchangé.
   */
  onStepClick?: (targetIndex: number) => void;
  /**
   * Predicate optionnel — retourne true si le broker peut sauter à `targetIndex`.
   * Sans predicate mais avec `onStepClick` : par défaut on autorise uniquement
   * les steps <= currentIndex (déjà complétés, backward-safe).
   */
  canGoToStep?: (targetIndex: number) => boolean;
};

export function StepIndicator({ steps, currentIndex, ariaLabel, onStepClick, canGoToStep }: Props) {
  const { t } = useTranslation();
  const isClickable = (idx: number): boolean => {
    if (!onStepClick) return false;
    if (idx === currentIndex) return false; // pas de re-click sur l'étape active
    if (canGoToStep) return canGoToStep(idx);
    // Défaut safe : seulement steps déjà complétés (backward-navigation).
    return idx < currentIndex;
  };
  return (
    <nav aria-label={ariaLabel ?? t("common.progress")} className="mb-6">
      <ol className="flex flex-wrap items-center gap-2 text-sm">
        {steps.map((label, idx) => {
          const isActive = idx === currentIndex;
          const isDone = idx < currentIndex;
          const clickable = isClickable(idx);

          const badgeClass = cn(
            "inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-full px-2 text-xs font-medium",
            isActive
              ? "bg-[color:var(--brand-primary)] text-[color:var(--brand-primary-foreground)]"
              : isDone
                ? "bg-slate-200 text-slate-700"
                : "bg-slate-100 text-slate-400",
          );
          const labelClass = cn(
            isActive ? "font-medium text-slate-900" : isDone ? "text-slate-600" : "text-slate-400",
          );

          const content = (
            <>
              <span aria-current={isActive ? "step" : undefined} className={badgeClass}>
                {idx + 1}
              </span>
              <span className={labelClass}>{label}</span>
            </>
          );

          return (
            <li key={idx} className="flex items-center gap-2">
              {clickable ? (
                <button
                  type="button"
                  onClick={() => onStepClick?.(idx)}
                  className="focus-visible:ring-brand-500 inline-flex items-center gap-2 rounded hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                >
                  {content}
                </button>
              ) : (
                <span className="inline-flex items-center gap-2">{content}</span>
              )}
              {idx < steps.length - 1 && (
                <span aria-hidden="true" className="text-slate-300">
                  ›
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
